import service from '.';

/**
 * UpsertArticleTemplateReq，创建文章模板
 */
export interface UpsertArticleTemplateReq {
  /**
   * 媒体组合
   */
  combinations: Combination[];
  industryIds?: Array<string | number>;
  remark?: string;
  /**
   * 模板Id
   */
  templateId?: string;
  /**
   * 模板名称
   */
  templateName?: string;
  [property: string]: any;
}

export interface ArticleTemplateIndustry {
  id?: string | number;
  industryId?: string | number;
  industriy?: string;
  industry?: string;
  label?: string;
  name?: string;
  value?: string | number;
}

export type TemplateMediaReplacementScope = 'SELECTED' | 'ALL';

export type MediaAvailability = 'AVAILABLE' | 'OFFLINE' | 'MISSING';

export interface TemplateSourceMediaItem {
  mediaId: string;
  mediaName: string;
  price?: number;
  channel?: string;
  displayChannel?: string;
  channelType?: string;
  availability: MediaAvailability;
  occurrenceCount: number;
}

export interface TemplateSourceMediaPageReq {
  scope: TemplateMediaReplacementScope;
  templateIds?: string[];
  mediaName?: string;
  pageIndex: number;
  pageSize: number;
}

export interface TemplateSourceMediaPageRes {
  scope: TemplateMediaReplacementScope;
  scopeTemplateCount: number;
  records: TemplateSourceMediaItem[];
  total: number;
}

export interface TemplateMediaReplacementGroupReq {
  sourceMediaIds: string[];
  targetMediaId: string;
}

export interface PreviewTemplateMediaReplacementReq {
  scope: TemplateMediaReplacementScope;
  templateIds?: string[];
  replacementGroups: TemplateMediaReplacementGroupReq[];
}

export interface TemplateMediaReplacementPreviewGroup {
  groupIndex: number;
  sourceMediaIds: string[];
  sourceMediaNames: string[];
  targetMediaId: string;
  targetMediaName: string;
  matchedTemplateCount: number;
  matchedItemCount: number;
}

export interface TemplateMediaReplacementPreviewRes {
  previewToken: string;
  scope: TemplateMediaReplacementScope;
  scopeTemplateCount: number;
  affectedTemplateCount: number;
  unaffectedTemplateCount: number;
  matchedItemCount: number;
  groups: TemplateMediaReplacementPreviewGroup[];
}

export type TemplateMediaReplacementResultStatus = 'SUCCESS' | 'SKIPPED' | 'FAILED' | 'NOT_EXECUTED';

export interface TemplateMediaReplacementResultItem {
  templateId: string;
  templateName: string;
  status: TemplateMediaReplacementResultStatus;
  replacedItemCount: number;
  beforeMinCost?: number;
  beforeMaxCost?: number;
  afterMinCost?: number;
  afterMaxCost?: number;
  reasonCode?: string;
  reason?: string;
}

export interface TemplateMediaReplacementResultRes {
  requestId: string;
  scope: TemplateMediaReplacementScope;
  scopeTemplateCount: number;
  successCount: number;
  skippedCount: number;
  failedCount: number;
  results: TemplateMediaReplacementResultItem[];
}

/**
 * Combination，组合项
 */
export interface Combination {
  /**
   * 组合Id
   */
  combinationId?: number;
  /**
   * 组合名称
   */
  combinationName: string;
  /**
   * 发文数量
   */
  count: number;
  /**
   * 组合项
   */
  items: ArticleItem[];
  [property: string]: any;
}

/**
 * ArticleItem，发布文章类型项
 */
export interface ArticleItem {
  /**
   * 文章类型Id
   */
  articleTypeId: number;
  /**
   * 组合项Id
   */
  id: string;
  /**
   * 媒体类型Id
   */
  mediaId: string;
  [property: string]: any;
}

/**
 * ArticleTemplateDetailRes，文章模板详情
 */
export interface ArticleTemplateDetailRes {
    /**
     * 文章数量
     */
    articleCount?: number;
    /**
     * 文章组合
     */
    combinations: ArticleCombinationRes[];
    industryIds?: Array<string | number>;
    industries?: ArticleTemplateIndustry[];
    /**
     * 最大成本金额
     */
    maxCost?: number;
    /**
     * 最小成本金额
     */
    minCost?: number;
    /**
     * 备注
     */
    remark?: string;
    /**
     * 模板Id
     */
    templateId?: number | string;
    /**
     * 模板名称
     */
    templateName?: string;
    status?: 'NORMAL' | 'CHANGE' | 'DISABLED'
    reason?: string;
    [property: string]: any;
}

/**
 * ArticleCombinationRes，文章模版组合接口返回参数
 */
export interface ArticleCombinationRes {
    /**
     * 文章模版组合文章数量
     */
    articleCount?: number;
    /**
     * 文章模版组合项
     */
    items?: ArticleCombinationItemRes[];
    /**
     * 文章模版组合文章价格最大区间
     */
    maxPrice?: number;
    /**
     * 文章模版组合文章价格最小区间
     */
    minPrice?: number;
    status?: 'NORMAL' | 'CHANGE' | 'DISABLED'
    [property: string]: any;
}

/**
 * ArticleCombinationItemRes，文章组合项响应
 */
export interface ArticleCombinationItemRes {
    /**
     * 文章类型id
     */
    articleTypeId?: string;
    /**
     * 文章类型名称
     */
    articleTypeName?: string;
    /**
     * 文章组合项id
     */
    id?: number;
    /**
     * 媒体id
     */
    mediaId: string;
    /**
     * 媒体名称
     */
    mediaName?: string;
    /**
     * 价格
     */
    price?: number;
    [property: string]: any;
}

export const articleTemplateApi = {
  list: (data: { pageIndex: number; pageSize: number; templateName?: string; industryIds?: string[] }) => {
    return service.post('/article/template-page', data);
  },
  get: (templateId: number | string) => {
    return service.get<ArticleTemplateDetailRes>('/article/template-detail', {
      params: { templateId },
    });
  },
  delete: (templateIds: string[]) => {
    return service.post('/article/template-delete', { templateIds });
  },
  save: (data: Partial<UpsertArticleTemplateReq>) => {
    return service.post('/article/template-upsert', data);
  },
  combinationPrice: (data: {
    /** 文章数量 */
    articleCount: number;
    /** 媒介ID */
    mediaIds: string[];
  }) => {
    return service.post<{
      minPrice: number;
      maxPrice: number;
    }>('/article/combination-price', data);
  },
  sourceMediaPage: (data: TemplateSourceMediaPageReq) => {
    return service.post<TemplateSourceMediaPageRes>('/article/template-media-replacement/source-media-page', data);
  },
  previewMediaReplacement: (data: PreviewTemplateMediaReplacementReq) => {
    return service.post<TemplateMediaReplacementPreviewRes>('/article/template-media-replacement/preview', data);
  },
  executeMediaReplacement: (data: { requestId: string; previewToken: string }) => {
    return service.post<TemplateMediaReplacementResultRes>('/article/template-media-replacement/execute', data);
  },
};
